// theme-sources/js/theme/components/cart-cross-sells.js

import { publish, THEME_EVENTS } from "../../shared/events";
import { fetchConfig } from "../core/utils";
import { syncCartFakeItem } from "../features/cart-fake-item";
import { logger } from "../core/logger";

class CartCrossSells extends HTMLElement {
  connectedCallback() {
    this.cartDrawer = document.querySelector("cart-drawer");
    this.ziggyDrawer = document.querySelector("ziggy-drawer[name='cart']");
    this.onClick = this.onClick.bind(this);

    this.addEventListener("click", this.onClick);

    requestAnimationFrame(() => {
      this.runMiniSwiperInit(this);
    });

    logger.info("CartCrossSells", "connected");
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.onClick);
    logger.info("CartCrossSells", "disconnected");
  }

  runMiniSwiperInit(root = this) {
    const init = window?.Ziggy?.initMiniSwipers;

    if (typeof init !== "function") {
      logger.warn(
        "CartCrossSells",
        "initMiniSwipers not available on window.Ziggy",
      );
      return;
    }

    init(root);
  }

  handleTabClick(tab) {
    const wrapper = tab.closest(".mini-cart-upsell");
    if (!wrapper) return;

    const target = tab.dataset.upsellTab;

    wrapper.querySelectorAll("[data-upsell-tab]").forEach((el) => {
      el.classList.toggle("is-active", el === tab);
    });

    wrapper.querySelectorAll("[data-upsell-panel]").forEach((panel) => {
      const isActive = panel.dataset.upsellPanel === target;
      panel.classList.toggle("is-active", isActive);
    });
  }

  getButtonProperties(button) {
    const raw = button.dataset.properties;

    if (!raw) return {};

    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (error) {
      logger.warn("CartCrossSells", "invalid data-properties JSON", {
        raw,
        error,
      });
      return {};
    }
  }

  async onClick(event) {
    const tab = event.target.closest("[data-upsell-tab]");
    if (tab && this.contains(tab)) {
      this.handleTabClick(tab);
      return;
    }

    const button = event.target.closest("[data-cart-upsell-add]");
    if (!button || !this.contains(button)) return;

    event.preventDefault();

    const variantId = Number(button.dataset.variantId);
    const properties = this.getButtonProperties(button);

    if (!variantId) {
      logger.warn("CartCrossSells", "click: invalid variantId");
      return;
    }

    if (!this.cartDrawer) {
      logger.warn("CartCrossSells", "click: cartDrawer not found");
      return;
    }

    button.disabled = true;
    this.classList.add("is-loading");

    try {
      const sections = this.cartDrawer
        .getSectionsToRender()
        .map((section) => section.id);

      logger.info("CartCrossSells", "adding upsell item", {
        variantId,
        properties,
        sections,
      });

      const response = await fetch(`${theme.routes.cart_add_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties }],
          sections,
          sections_url: window.location.pathname,
        }),
      });

      const baseState = await response.json();

      if (!response.ok) {
        throw new Error(
          `Upsell add failed (${response.status}) ${baseState?.description || baseState?.message || ""}`,
        );
      }

      const finalState = await syncCartFakeItem({
        baseState,
        sections,
        sectionsUrl: window.location.pathname,
      });

      publish(THEME_EVENTS.cartUpdate, {
        source: "cart-cross-sells",
        productVariantId: variantId,
        cartData: finalState,
      });

      const drawerSectionId = this.cartDrawer.dataset.sectionId;
      const hasRenderableSections =
        !!finalState?.sections &&
        !!drawerSectionId &&
        !!finalState.sections[drawerSectionId] &&
        !!finalState.sections["cart-icon-bubble"];

      if (hasRenderableSections) {
        this.cartDrawer.renderContents(finalState);

        requestAnimationFrame(() => {
          const nextCrossSells =
            this.cartDrawer.querySelector("cart-cross-sells");

          if (nextCrossSells) {
            this.runMiniSwiperInit(nextCrossSells);
          } else {
            this.runMiniSwiperInit(this.cartDrawer);
          }
        });
      }

      this.ziggyDrawer?.open();

      logger.info("CartCrossSells", "add success", {
        variantId,
        properties,
        itemCount: finalState?.item_count,
      });
    } catch (error) {
      logger.error("CartCrossSells", "onClick error", error);
    } finally {
      button.disabled = false;
      this.classList.remove("is-loading");
    }
  }
}

if (!customElements.get("cart-cross-sells")) {
  customElements.define("cart-cross-sells", CartCrossSells);
}
